import { Crop } from 'react-image-crop';

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas');

  canvas.width = width;
  canvas.height = height;

  return canvas;
};

export const fileToImage = (file?: File) =>
  file ? URL.createObjectURL(new Blob([file], { type: file.type })) : '';

export const imgSrcToImage = (imgSrc: string): HTMLImageElement => {
  const image = new Image();
  image.src = imgSrc;

  return image;
};

export const resizeImage = (
  imgSrc: HTMLImageElement | string,
  scale: number,
  format = 'image/jpeg',
) => {
  const image = typeof imgSrc === 'string' ? imgSrcToImage(imgSrc) : imgSrc;

  const canvas = createCanvas(
    parseInt(`${image.width * scale}`),
    parseInt(`${image.height * scale}`),
  );

  const ctx = canvas.getContext('2d');
  ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL(format);
};

export const getCroppedImgFromOriginal = (
  imageSrc: string | HTMLImageElement,
  crop: Crop,
  scale: number,
  format = 'image/jpeg',
) => {
  const croppedImageWidth = crop.width / scale;
  const croppedImageHeight = crop.height / scale;
  const croppedImage = createCanvas(croppedImageWidth, croppedImageHeight);
  const sourceImage =
    typeof imageSrc === 'string' ? imgSrcToImage(imageSrc) : imageSrc;
  const startCoordinateX = crop.x / scale;
  const startCoordinateY = crop.y / scale;

  croppedImage
    .getContext('2d')
    ?.drawImage(
      sourceImage,
      startCoordinateX,
      startCoordinateY,
      croppedImageWidth,
      croppedImageHeight,
      0,
      0,
      croppedImageWidth,
      croppedImageHeight,
    );

  return croppedImage.toDataURL(format);
};

export const calculateCenteredCrop = (
  imageWidth: number,
  imageHeight: number,
  aspectRatio: number,
): {
  x: number;
  y: number;
  width: number;
  height: number;
} => {
  const imageAspectRatio = imageWidth / imageHeight;

  let cropWidth: number;
  let cropHeight: number;

  if (imageAspectRatio > aspectRatio) {
    cropHeight = imageHeight;
    cropWidth = cropHeight * aspectRatio;
  } else {
    cropWidth = imageWidth;
    cropHeight = cropWidth / aspectRatio;
  }

  const cropX = (imageWidth - cropWidth) / 2;
  const cropY = (imageHeight - cropHeight) / 2;

  return {
    x: cropX,
    y: cropY,
    width: cropWidth,
    height: cropHeight,
  };
};

/**
 * 자른 영역의 가로세로비를 허용 범위 안으로 되돌린다.
 * 범위를 넘은 쪽의 변을 줄이는 방향으로만 보정하므로, 결과가 원본 이미지 밖으로 나가지 않는다.
 */
export const clampCropToAspectRange = <T extends Crop>(
  crop: T,
  range: { min: number; max: number },
): T => {
  if (!crop.width || !crop.height) {
    return crop;
  }

  const ratio = crop.width / crop.height;

  if (ratio > range.max) {
    return { ...crop, width: crop.height * range.max };
  }

  if (ratio < range.min) {
    return { ...crop, height: crop.width / range.min };
  }

  return crop;
};

/**
 * 자른 영역이 썸네일로 쓰기에 너무 작은지 판정한다.
 * 목록 카드는 이미지를 카드 폭에 맞춰 늘리므로, 비율이 자유로울 때는 가로만 본다.
 * 세로는 비율에 따라 달라질 뿐 표시 크기를 결정하지 않는다.
 */
export const isBelowMinimumSize = (
  width: number,
  height: number,
  minWidth?: number,
  minHeight?: number,
  isFreeAspect = false,
): boolean => {
  if (!minWidth || !minHeight) {
    return false;
  }

  if (isFreeAspect) {
    return width < minWidth;
  }

  return width < minWidth || height < minHeight;
};
